import { z } from 'zod';

/**
 * Rich text as TipTap/ProseMirror JSON (project and task descriptions, later comments). The
 * server validates the structure so that only known nodes and marks are stored; the editor
 * (TipTap, phase 3b) must not offer anything outside this list.
 */

export const richTextNodeTypes = [
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'hardBreak',
  'horizontalRule',
] as const;

/** Upper bounds against oversized or deeply nested documents. */
export const RICH_TEXT_MAX_DEPTH = 12;
export const RICH_TEXT_MAX_TEXT_LENGTH = 100_000;
const MAX_CHILDREN = 2000;

const attrValueSchema = z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]);

const linkHrefSchema = z
  .string()
  .max(2000)
  .refine((href) => /^(https?:|mailto:)/i.test(href), 'Only http(s) and mailto links');

/** Links keep only `href` (unknown attributes such as `target` are stripped). */
const markSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('link'), attrs: z.object({ href: linkHrefSchema }) }),
  z.object({ type: z.enum(['bold', 'italic', 'underline', 'strike', 'code']) }).strict(),
]);

export type RichTextMark = z.infer<typeof markSchema>;

/** Comments additionally allow @mentions (COL-01); descriptions do not. */
export const commentNodeTypes = [...richTextNodeTypes, 'mention'] as const;

export type RichTextNode = {
  type: (typeof commentNodeTypes)[number];
  attrs?: Record<string, string | number | boolean | null> | undefined;
  content?: RichTextNode[] | undefined;
  text?: string | undefined;
  marks?: RichTextMark[] | undefined;
};

function nodeSchemaFor(types: readonly [string, ...string[]]): z.ZodType<RichTextNode> {
  const node: z.ZodType<RichTextNode> = z.lazy(() =>
    z
      .object({
        type: z.enum(types),
        attrs: z.record(z.string().max(50), attrValueSchema).optional(),
        content: z.array(node).max(MAX_CHILDREN).optional(),
        text: z.string().optional(),
        marks: z.array(markSchema).max(10).optional(),
      })
      .strict()
      .refine((n) => (n.type === 'text') === (n.text !== undefined), {
        message: 'Only text nodes carry text',
      }),
  ) as z.ZodType<RichTextNode>; // z.enum(types) yields string; `types` only holds node types
  return node;
}

function depth(node: RichTextNode): number {
  return 1 + Math.max(0, ...(node.content ?? []).map(depth));
}

function docSchemaFor(types: readonly [string, ...string[]]) {
  const node = nodeSchemaFor(types);
  return z
    .object({ type: z.literal('doc'), content: z.array(node).max(MAX_CHILDREN).optional() })
    .strict()
    .superRefine((doc, issues) => {
      if (depth(doc) > RICH_TEXT_MAX_DEPTH) {
        issues.addIssue({ code: 'custom', message: 'Document is nested too deeply' });
      }
      if (richTextToPlainText(doc).length > RICH_TEXT_MAX_TEXT_LENGTH) {
        issues.addIssue({ code: 'custom', message: 'Document is too long' });
      }
    });
}

export const richTextSchema = docSchemaFor(richTextNodeTypes);
export type RichText = z.infer<typeof richTextSchema>;

const blockTypes = new Set<string>([
  'paragraph',
  'heading',
  'listItem',
  'blockquote',
  'codeBlock',
  'horizontalRule',
]);

/** Plain text for search and previews: blocks separated by newlines. */
export function richTextToPlainText(doc: { content?: RichTextNode[] | undefined }): string {
  const blocks: string[] = [];
  let line = '';
  const flush = () => {
    if (line !== '') blocks.push(line);
    line = '';
  };
  const walk = (node: RichTextNode) => {
    if (node.type === 'text') line += node.text ?? '';
    else if (node.type === 'mention') line += `@${String(node.attrs?.['label'] ?? '')}`;
    else if (node.type === 'hardBreak') line += '\n';
    for (const child of node.content ?? []) walk(child);
    if (blockTypes.has(node.type)) flush();
  };
  for (const node of doc.content ?? []) walk(node);
  flush();
  return blocks.join('\n');
}

/** One paragraph per line; empty lines are dropped. */
export function plainTextToRichText(text: string): RichText {
  const content: RichTextNode[] = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => ({ type: 'paragraph', content: [{ type: 'text', text: line }] }));
  return { type: 'doc', content };
}

// --- Comments (COL-01) -------------------------------------------------------------------------

function walkNodes(
  nodes: readonly RichTextNode[] | undefined,
  visit: (node: RichTextNode) => void,
) {
  for (const node of nodes ?? []) {
    visit(node);
    walkNodes(node.content, visit);
  }
}

/**
 * Comment bodies as stored and returned. Mention nodes carry `id` (user id; `null` with an empty
 * `label` when the viewer may not learn who it is, E-08) and `label` (name, set by the server).
 */
export const commentRichTextSchema = docSchemaFor(commentNodeTypes);
export type CommentRichText = z.infer<typeof commentRichTextSchema>;

/** Input: every mention names a user id; the label is replaced by the server. */
export const commentBodyInputSchema = commentRichTextSchema.superRefine((doc, issues) => {
  const content = { found: false };
  walkNodes(doc.content, (node) => {
    if (node.type === 'mention') {
      content.found = true;
      const attrs = node.attrs ?? {};
      if (!z.uuid().safeParse(attrs['id']).success || node.content !== undefined) {
        issues.addIssue({ code: 'custom', message: 'Mentions need a user id' });
      }
    } else if (node.type === 'text' && (node.text ?? '').trim() !== '') {
      content.found = true;
    }
  });
  if (!content.found) issues.addIssue({ code: 'custom', message: 'Comment is empty' });
});

/** Distinct user ids mentioned in a comment, in order of appearance. */
export function extractMentionIds(doc: { content?: RichTextNode[] | undefined }): string[] {
  const ids: string[] = [];
  walkNodes(doc.content, (node) => {
    const id = node.attrs?.['id'];
    if (node.type === 'mention' && typeof id === 'string' && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

/**
 * Copy of the document with every mention's `id` and `label` replaced by `resolve` (current
 * names, hidden people). Mentions `resolve` does not know lose id and label, so a stored name
 * never reaches a viewer who may not see it.
 */
export function mapMentions<T extends { content?: RichTextNode[] | undefined }>(
  doc: T,
  resolve: (id: string) => { id: string | null; label: string } | undefined,
): T {
  const map = (node: RichTextNode): RichTextNode => {
    if (node.type === 'mention') {
      const id = node.attrs?.['id'];
      const resolved = typeof id === 'string' ? resolve(id) : undefined;
      return {
        type: 'mention',
        attrs: resolved ?? { id: null, label: '' },
      };
    }
    return node.content === undefined ? node : { ...node, content: node.content.map(map) };
  };
  return doc.content === undefined ? doc : { ...doc, content: doc.content.map(map) };
}
